import React, { useEffect, useState } from "react";
import { closeModal, LoadingDiv, SweetAlertShow } from "../master/Master";
import {
  expenseCategoryList,
  ExpenseTransactionCreate,
  ExpenseTransactionDelete,
  ExpenseTransactionList,
  ExpenseTransactionUpdate,
} from "../../api/Expenses";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Swal from "sweetalert2";
import { StaffList } from "../../api/Staff";
import { BankList } from "../../api/BankService";
import usePrivateLink from "../config/usePrivateLink";
import Pagination from "../components/Pagination";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { formatToDate } from "../master/FormatToDateTime";
import ExpenseLayout from "./ExpenseLayout";
import { imgBaseUrl } from "../../api/api";

export default function Expenses() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const [expenses, setExpenses] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [data, setData] = useState(null);

  const [modalData, setModalData] = useState(null);
  const [months, setMonths] = useState([]);

  const currentMonth = new Date();

  const currentMonthValue = `${currentMonth.getFullYear()}-${String(
    currentMonth.getMonth() + 1,
  ).padStart(2, "0")}`;

  const [filters, setFilters] = useState({
    month: currentMonthValue,
  });

  /* ============================================================
     GET BANKS
  ============================================================ */

  const getBusinessBanks = async () => {
    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load banks",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    }
  };

  /* ============================================================
     GET EXPENSE CATEGORIES
  ============================================================ */

  const getExpenseCategory = async () => {
    try {
      const response = await expenseCategoryList();

      if (response?.data?.status) {
        setExpenses(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load categories",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    }
  };

  /* ============================================================
     GET STAFFS
  ============================================================ */

  const getStaffs = async () => {
    try {
      const response = await StaffList();

      if (response?.data?.status) {
        setStaffs(response?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load staff",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    }
  };

  /* ============================================================
     GET TRANSACTIONS
  ============================================================ */

  const getTransactions = async () => {
    setTableLoading(true);

    try {
      let addUrl = "type=Normal";

      Object.keys(filters || {}).forEach((key) => {
        const value = filters[key];

        if (value !== undefined && value !== null && value !== "") {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await ExpenseTransactionList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || null);
      } else {
        setData(null);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No data found",
        );
      }
    } catch (error) {
      setData(null);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setTableLoading(false);
    }
  };

  /* ============================================================
     INITIAL DATA
  ============================================================ */

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      try {
        await Promise.all([
          getBusinessBanks(),
          getStaffs(),
          getExpenseCategory(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  /* ============================================================
     TRANSACTION FILTER CHANGE
  ============================================================ */

  useEffect(() => {
    getTransactions();
  }, [filters]);

  /* ============================================================
     MONTH LIST
  ============================================================ */

  useEffect(() => {
    const list = [];
    const current = new Date();

    for (let i = 0; i < 24; i++) {
      const d = new Date(current.getFullYear(), current.getMonth() - i, 1);

      list.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}`,
        label: d.toLocaleDateString("en-IN", {
          month: "long",
          year: "numeric",
        }),
      });
    }

    setMonths(list);
  }, []);

  /* ============================================================
     ADD EXPENSE
  ============================================================ */

  const AddExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseTransactionCreate(formData);

      if (response?.data?.status) {
        e.target.reset();

        setModalData({
          expense_id: "",
        });

        SweetAlertShow("success", "Successfully", response?.data?.message);

        getTransactions();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     UPDATE EXPENSE
  ============================================================ */

  const UpdateExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseTransactionUpdate(formData);

      if (response?.data?.status) {
        closeModal("Edit");

        e.target.reset();

        setModalData(null);

        await getTransactions();

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     DELETE EXPENSE
  ============================================================ */

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const response = await ExpenseTransactionDelete(id);

      if (response?.data?.status) {
        await getTransactions();

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     DOWNLOAD / PRINT
  ============================================================ */

  const handleDownload = async (type = "excel") => {
    setLoading(true);

    try {
      const downloadData = [];
      const lastPage = data?.data?.last_page ?? 1;

      for (let page = 1; page <= lastPage; page++) {
        let addUrl = `page=${page}&type=Normal`;

        Object.keys(filters || {}).forEach((key) => {
          const value = filters[key];

          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            key !== "page"
          ) {
            addUrl += `&${key}=${encodeURIComponent(value)}`;
          }
        });

        const response = await ExpenseTransactionList(addUrl);

        if (response?.data?.status) {
          const items = Array.isArray(response?.data?.data?.data?.data)
            ? response.data.data.data.data
            : [];

          const pageRows = items.map((item) => ({
            "Expense Category": item?.expense?.title ?? "",
            Date: formatToDate(item?.date) ?? "",
            Amount: Number(item?.amount ?? 0),
            "Payment Account": item?.bank?.account_no ?? "",
            "Account Holder": item?.bank?.owner_name ?? "",
            Description: item?.description ?? "",
            "Staff Name": item?.staff?.name ?? "",
            "Staff Mobile": item?.staff?.mobile ?? "",
          }));

          downloadData.push(...pageRows);
        } else {
          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "No data",
          );

          break;
        }
      }

      if (downloadData.length > 0) {
        if (type === "excel") {
          const now = new Date();

          const fileName = `Report_${now.getFullYear()}-${now.getMonth() + 1
            }-${now.getDate()}_${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found to download.");
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     RESET FILTERS
  ============================================================ */

  const resetFilters = () => {
    setFilters({
      month: currentMonthValue,
    });
  };

  /* ============================================================
     VIEW IMAGE URL
  ============================================================ */

  const getImageUrl = (image) => {
    if (!image) return "";

    return `${imgBaseUrl}${image}`;
  };

  const transactions = Array.isArray(data?.data?.data) ? data.data.data : [];

  const paginationData = data?.data || null;

  const totalAmount = Number(data?.totalAmount || 0);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <ExpenseLayout />

                  <div className="col-12">
                    {/* ==================================================
                        PAGE HEADER
                    ================================================== */}

                    <div className="card my-3 border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                          <div>
                            <h3 className="fw-bold mb-1">
                              Expense Transactions
                            </h3>

                            <p className="text-muted mb-0">
                              Manage all expense transactions and payments
                            </p>
                          </div>

                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              data-bs-toggle="offcanvas"
                              data-bs-target="#expenseFilterCanvas"
                            >
                              <i className="ri-filter-3-line me-2" />
                              Filters
                            </button>

                            {hasAccess("/expense/export") && (
                              <button
                                type="button"
                                className="btn btn-success"
                                disabled={loading}
                                onClick={() => handleDownload("excel")}
                              >
                                <i className="ri-file-excel-2-line me-2" />
                                Export
                              </button>
                            )}

                            {hasAccess("/expense/print") && (
                              <button
                                type="button"
                                className="btn btn-danger"
                                disabled={loading}
                                onClick={() => handleDownload("print")}
                              >
                                <i className="ri-printer-line me-2" />
                                Print
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        ADD EXPENSE
                    ================================================== */}
                    {hasAccess("/expenses/add") && (
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-header bg-primary text-white">
                          <h5 className="mb-0">
                            <i className="ri-add-circle-line me-2" />
                            Add Expense
                          </h5>
                        </div>

                        <div className="card-body pt-4">
                          <form onSubmit={AddExpenses}>
                            <input
                              type="hidden"
                              name="expense_type"
                              value="Normal"
                            />

                            <div className="row g-3">
                              {/* CATEGORY */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="expense_id"
                                    value={modalData?.expense_id || ""}
                                    onChange={(e) =>
                                      setModalData((prev) => ({
                                        ...prev,
                                        expense_id: e.target.value,
                                      }))
                                    }
                                    required
                                  >
                                    <option value="">
                                      Select Expense Category
                                    </option>

                                    {expenses.map((category) => (
                                      <option
                                        key={category.id}
                                        value={category.id}
                                      >
                                        {category.title}
                                      </option>
                                    ))}
                                  </select>

                                  <label>Category</label>
                                </div>
                              </div>

                              {/* STAFF */}
                              {(modalData?.expense_id === "1" ||
                                modalData?.expense_id === "2") && (
                                  <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                      <select
                                        className="form-select"
                                        name="staff_id"
                                      >
                                        <option value="">Select Staff</option>

                                        {staffs?.data?.map((staff) => (
                                          <option key={staff.id} value={staff.id}>
                                            {staff.name}
                                          </option>
                                        ))}
                                      </select>

                                      <label>Select Staff</label>
                                    </div>
                                  </div>
                                )}

                              {/* AMOUNT */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="amount"
                                    placeholder="Amount"
                                    step="0.01"
                                    min="0"
                                    required
                                  />

                                  <label>Amount</label>
                                </div>
                              </div>

                              {/* DATE */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="date"
                                    className="form-control"
                                    name="date"
                                    required
                                  />

                                  <label>Date</label>
                                </div>
                              </div>

                              {/* IMAGE */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="file"
                                    className="form-control"
                                    name="img"
                                    accept="image/*"
                                    capture="environment"
                                  />

                                  <label>Image</label>
                                </div>
                              </div>

                              {/* BANK */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="bank_id"
                                    required
                                  >
                                    <option value="">Select Bank</option>

                                    {banks.map((item) => (
                                      <option key={item.id} value={item.id}>
                                        {item.account_no} ({item.owner_name})
                                      </option>
                                    ))}
                                  </select>

                                  <label>Select Account</label>
                                </div>
                              </div>

                              {/* DESCRIPTION */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <textarea
                                    className="form-control"
                                    rows="2"
                                    name="description"
                                    placeholder="Remark"
                                  />

                                  <label>Remark</label>
                                </div>
                              </div>

                              {/* STATUS */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="status"
                                    defaultValue="1"
                                    required
                                  >
                                    <option value="1">Complete</option>

                                    <option value="2">Pending</option>
                                  </select>

                                  <label>Payment Status</label>
                                </div>
                              </div>
                            </div>

                            <div className="text-end mt-4">
                              <button
                                type="submit"
                                className="btn btn-primary px-5"
                                disabled={loading}
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-line me-2" />
                                    Save Expense
                                  </>
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                    
                    {/* ==================================================
                        TRANSACTIONS
                    ================================================== */}

                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                          <div>
                            <h5 className="text-success fw-bold mb-0">
                              Total Expense: ₹{" "}
                              {totalAmount.toLocaleString("en-IN")}
                            </h5>

                            <small className="text-muted">
                              Expense Transactions
                            </small>
                          </div>

                          <div style={{ minWidth: 220 }}>
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.month || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    month: e.target.value,
                                    page: 1,
                                  }))
                                }
                              >
                                <option value="">All Months</option>

                                {months.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>

                              <label>Month</label>
                            </div>
                          </div>
                        </div>

                        {/* TABLE */}

                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead
                              className="table-light"
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 5,
                              }}
                            >
                              <tr className="text-nowrap">
                                <th width="60">#</th>
                                <th>Amount</th>
                                <th>Date</th>
                                <th>Expense Category</th>
                                <th>Payment Type</th>
                                <th>Remark</th>
                                <th>Payment Status</th>
                                <th width="150" className="text-center">
                                  Action
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {/* LOADING */}
                              {tableLoading ? (
                                <tr>
                                  <td colSpan="8" className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    />

                                    <div className="mt-2 text-muted">
                                      Loading transactions...
                                    </div>
                                  </td>
                                </tr>
                              ) : transactions.length > 0 ? (
                                transactions.map((item, index) => (
                                  <tr key={item.id}>
                                    {/* INDEX */}
                                    <td className="fw-semibold">{index + 1}</td>

                                    {/* AMOUNT */}
                                    <td>
                                      <span className="badge rounded-pill bg-label-success fs-6">
                                        ₹{" "}
                                        {Number(
                                          item.amount || 0,
                                        ).toLocaleString("en-IN")}
                                      </span>
                                    </td>

                                    {/* DATE */}
                                    <td>
                                      <div className="fw-medium text-nowrap">
                                        {formatToDate(item.date)}
                                      </div>
                                    </td>

                                    {/* CATEGORY */}
                                    <td>
                                      <div className="fw-semibold">
                                        {item.expense?.title || "-"}
                                      </div>
                                    </td>

                                    {/* PAYMENT */}
                                    <td>
                                      <div className="fw-semibold">
                                        {item.bank?.owner_name || "-"}
                                      </div>

                                      <small className="text-muted">
                                        {item.bank?.account_no || "-"}
                                      </small>
                                    </td>

                                    {/* REMARK */}
                                    <td>
                                      {item.staff?.name && (
                                        <div className="fw-semibold">
                                          ({item.staff.name})
                                        </div>
                                      )}

                                      <div
                                        className="small text-muted"
                                        style={{
                                          maxWidth: 250,
                                        }}
                                      >
                                        {item.description || "-"}
                                      </div>
                                    </td>

                                    {/* STATUS */}
                                    <td>
                                      {item.status === "1" ? (
                                        <span className="badge rounded-pill bg-success">
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="badge rounded-pill bg-warning">
                                          Pending
                                        </span>
                                      )}
                                    </td>

                                    {/* ACTION */}
                                    <td>
                                      <div className="d-flex justify-content-center gap-2">
                                        {/* VIEW */}
                                         {hasAccess("/expenses/view") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-info"
                                          data-bs-toggle="modal"
                                          data-bs-target="#ViewExpense"
                                          onClick={() => setModalData(item)}
                                          title="View Expense"
                                        >
                                          <i className="ri-eye-line" />
                                        </button>
                                        )}

                                        {/* EDIT */}
                                        {hasAccess("/expenses/update") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            data-bs-toggle="modal"
                                            data-bs-target="#Edit"
                                            onClick={() => setModalData(item)}
                                            title="Edit Expense"
                                          >
                                            <i className="ri-pencil-line" />
                                          </button>
                                        )}

                                        {/* DELETE */}
                                        {hasAccess("/expenses/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            title="Delete Expense"
                                            onClick={() => {
                                              Swal.fire({
                                                title: "Are you sure?",
                                                text: "This action will permanently delete!",
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#d33",
                                                cancelButtonColor: "#6c757d",
                                                confirmButtonText:
                                                  "Yes, delete it!",
                                                cancelButtonText: "Cancel",
                                              }).then((result) => {
                                                if (result.isConfirmed) {
                                                  handleDelete(item.id);
                                                }
                                              });
                                            }}
                                          >
                                            <i className="ri-delete-bin-line" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                /* EMPTY */
                                <tr>
                                  <td colSpan="8" className="text-center py-5">
                                    <i
                                      className="ri-inbox-2-line text-secondary"
                                      style={{
                                        fontSize: 50,
                                      }}
                                    />

                                    <h5 className="mt-3">
                                      No Transactions Found
                                    </h5>

                                    <p className="text-muted mb-0">
                                      No expense transaction matches your
                                      filters.
                                    </p>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>

                          {!tableLoading && (
                            <Pagination
                              data={paginationData}
                              setFilters={setFilters}
                              filters={filters}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          VIEW EXPENSE MODAL
      ============================================================ */}

      <div
        className="modal fade"
        id="ViewExpense"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-label-info">
              <div>
                <h5 className="modal-title fw-bold mb-1">Expense Details</h5>

                <small className="text-muted">
                  Transaction #{modalData?.id || "-"}
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>

            <div className="modal-body">
              {!modalData ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <div className="row g-3">
                  {/* AMOUNT */}
                  <div className="col-md-6">
                    <div className="border rounded p-3 h-100">
                      <small className="text-muted">Amount</small>

                      <h4 className="text-success mb-0 mt-1">
                        ₹{" "}
                        {Number(modalData.amount || 0).toLocaleString("en-IN")}
                      </h4>
                    </div>
                  </div>

                  {/* STATUS */}
                  <div className="col-md-6">
                    <div className="border rounded p-3 h-100">
                      <small className="text-muted">Payment Status</small>

                      <div className="mt-2">
                        {modalData.status === "1" ? (
                          <span className="badge rounded-pill bg-success">
                            Complete
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-warning">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* CATEGORY */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Expense Category</small>

                      <div className="fw-semibold mt-1">
                        {modalData.expense?.title || "-"}
                      </div>
                    </div>
                  </div>

                  {/* DATE */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Date</small>

                      <div className="fw-semibold mt-1">
                        {modalData.date ? formatToDate(modalData.date) : "-"}
                      </div>
                    </div>
                  </div>

                  {/* ACCOUNT */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Payment Account</small>

                      <div className="fw-semibold mt-1">
                        {modalData.bank?.account_no || "-"}
                      </div>

                      <small className="text-muted">
                        {modalData.bank?.owner_name || ""}
                      </small>
                    </div>
                  </div>

                  {/* BANK */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Bank Details</small>

                      <div className="fw-semibold mt-1">
                        {modalData.bank?.branch || "-"}
                      </div>

                      <small className="text-muted">
                        IFSC: {modalData.bank?.ifsc_code || "-"}
                      </small>
                    </div>
                  </div>

                  {/* STAFF */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Staff</small>

                      <div className="fw-semibold mt-1">
                        {modalData.staff?.name || "Not Applicable"}
                      </div>

                      {modalData.staff?.mobile && (
                        <small className="text-muted">
                          {modalData.staff.mobile}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* PAYMENT TYPE */}
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted">Payment Type</small>

                      <div className="fw-semibold mt-1">
                        {modalData.payment_type || "-"}
                      </div>
                    </div>
                  </div>

                  {/* DESCRIPTION */}
                  <div className="col-12">
                    <div className="border rounded p-3">
                      <small className="text-muted">Description / Remark</small>

                      <div className="mt-1">
                        {modalData.description || "No description"}
                      </div>
                    </div>
                  </div>

                  {/* IMAGE */}
                  <div className="col-12">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-3">
                        Expense Image
                      </small>

                      {modalData.image ? (
                        <div className="text-center">
                          <img
                            src={getImageUrl(modalData.image)}
                            alt="Expense"
                            className="img-fluid rounded border"
                            style={{
                              maxHeight: 450,
                              maxWidth: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i
                            className="ri-image-line d-block mb-2"
                            style={{
                              fontSize: 45,
                            }}
                          />
                          No expense image available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          FILTER OFFCANVAS
      ============================================================ */}

      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="expenseFilterCanvas"
        aria-labelledby="expenseFilterCanvasLabel"
      >
        <div className="offcanvas-header border-bottom">
          <div>
            <h5
              className="offcanvas-title fw-bold"
              id="expenseFilterCanvasLabel"
            >
              Expense Filters
            </h5>

            <small className="text-muted">Filter expense transactions</small>
          </div>

          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
          />
        </div>

        <div className="offcanvas-body">
          {/* CATEGORY */}
          <div className="form-floating form-floating-outline mb-3">
            <select
              className="form-select"
              value={filters?.expense_id || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  expense_id: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">All Categories</option>

              {expenses.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>

            <label>Expense Category</label>
          </div>

          {/* STAFF */}
          <div className="form-floating form-floating-outline mb-3">
            <select
              className="form-select"
              value={filters?.staff_id || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  staff_id: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">All Staff</option>

              {staffs?.data?.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>

            <label>Staff</label>
          </div>

          {/* BANK */}
          <div className="form-floating form-floating-outline mb-3">
            <select
              className="form-select"
              value={filters?.bank_id || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  bank_id: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">All Accounts</option>

              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.account_no} ({bank.owner_name})
                </option>
              ))}
            </select>

            <label>Payment Account</label>
          </div>

          {/* FROM DATE */}
          <div className="form-floating form-floating-outline mb-3">
            <input
              type="date"
              className="form-control"
              value={filters?.start_date || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                  page: 1,
                }))
              }
            />

            <label>From Date</label>
          </div>

          {/* TO DATE */}
          <div className="form-floating form-floating-outline mb-4">
            <input
              type="date"
              className="form-control"
              value={filters?.end_date || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                  page: 1,
                }))
              }
            />

            <label>To Date</label>
          </div>

          <hr />

          <div className="d-grid gap-2">
            <button
              type="button"
              className="btn btn-primary"
              data-bs-dismiss="offcanvas"
            >
              <i className="ri-check-line me-2" />
              Apply Filters
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              <i className="ri-refresh-line me-2" />
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================
          EDIT MODAL
      ============================================================ */}

      <div className="modal fade" id="Edit" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={UpdateExpenses}>
              <div className="modal-header bg-label-primary p-3">
                <h5 className="modal-title">Update Expense</h5>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                />
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <input
                    type="hidden"
                    name="id"
                    value={modalData?.id || ""}
                    readOnly
                  />

                  {/* CATEGORY */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="expense_id"
                        value={modalData?.expense_id || ""}
                        onChange={(e) =>
                          setModalData((prev) => ({
                            ...prev,
                            expense_id: e.target.value,
                          }))
                        }
                        required
                      >
                        <option value="">Select Expense Category</option>

                        {expenses.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.title}
                          </option>
                        ))}
                      </select>

                      <label>Category</label>
                    </div>
                  </div>

                  {/* STAFF */}
                  {(String(modalData?.expense_id) === "1" ||
                    String(modalData?.expense_id) === "2") && (
                      <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            name="staff_id"
                            value={modalData?.staff_id || ""}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                staff_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select Staff</option>

                            {staffs?.data?.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}
                              </option>
                            ))}
                          </select>

                          <label>Select Staff</label>
                        </div>
                      </div>
                    )}

                  {/* AMOUNT */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        step="0.01"
                        min="0"
                        defaultValue={modalData?.amount || ""}
                        required
                      />

                      <label>Amount</label>
                    </div>
                  </div>

                  {/* DATE */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        defaultValue={modalData?.date || ""}
                        required
                      />

                      <label>Date</label>
                    </div>
                  </div>

                  {/* BANK */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="bank_id"
                        defaultValue={modalData?.bank_id || ""}
                        required
                      >
                        <option value="">Select Bank</option>

                        {banks.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.account_no} ({item.owner_name})
                          </option>
                        ))}
                      </select>

                      <label>Select Account</label>
                    </div>
                  </div>

                  {/* DESCRIPTION */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <textarea
                        className="form-control"
                        rows="2"
                        name="description"
                        defaultValue={modalData?.description || ""}
                        style={{
                          minHeight: 100,
                        }}
                      />

                      <label>Remark</label>
                    </div>
                  </div>

                  {/* STATUS */}
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="status"
                        defaultValue={modalData?.status || "1"}
                        required
                      >
                        <option value="1">Complete</option>

                        <option value="2">Pending</option>
                      </select>

                      <label>Payment Status</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-label-primary py-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-2" />
                      Update Expense
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
